import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  AttributeDto,
  AttributeOptionDto,
  AttributeType,
  ProductAttributeSlotDto,
  ProductAttributeValueInput,
} from '@ecom/contracts';
import { useAttributesStore } from '@/stores/attributes.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore, type AdminLocale } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { CheckIcon, PlusIcon, SearchIcon, TagIcon, TrashIcon } from '@/components/icons';
import { Section, Segmented, Sheet } from '@/components/page';
import { Alert, Button, EmptyState, Field, IconButton, Input, Pill, Select, Switch, cx } from '@/components/ui';

/**
 * The specifications on one product (ADR-053).
 *
 * The category suggests; the product decides. The first group is what the
 * product's category and its ancestors ask — every product in a category
 * answers the same questions without anyone configuring the form — and the
 * second is what the operator added to this product from the library, searched
 * or created on the spot without leaving the product. A value is what makes a
 * specification the product's own: an added one with nothing in it is not
 * saved, and removing one clears it.
 *
 * A retired attribute is no longer asked, but a product that still holds a
 * value for one sees it flagged, to clear — hidden, it would be uneditable.
 *
 * Saved as a whole: the form is one form, and a partial save would leave a
 * cleared field still answered. While a product is being created there is no
 * save of its own; every change is reported upward and the product's save
 * applies it.
 */
export function SpecificationsCard({
  productId,
  categoryId,
  onDraftChange,
}: {
  /** Set when editing a saved product. */
  productId?: string;
  /** Set instead while creating one: the suggestions come from the category. */
  categoryId?: string | null;
  /** Called on every change while creating, so the editor can apply it after save. */
  onDraftChange?: (values: ProductAttributeValueInput[]) => void;
}) {
  const {
    library,
    productSlots,
    productSlotsFor,
    categoryAttributes,
    categoryAttributesFor,
    saving,
    error,
    load,
    loadForProduct,
    loadForCategory,
    setForProduct,
  } = useAttributesStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  /** Changes over what was loaded, by attribute. */
  const [edits, setEdits] = useState<Record<string, ProductAttributeValueInput>>({});
  /** Picked from the library on this visit; kept here, empty, until they hold a value. */
  const [added, setAdded] = useState<string[]>([]);
  /** The product's own specifications removed on this visit — cleared by the next save. */
  const [dropped, setDropped] = useState<string[]>([]);
  const [picking, setPicking] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // The library names every row in the reader's language and feeds the picker.
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (productId) void loadForProduct(productId);
    else if (categoryId) void loadForCategory(categoryId);
  }, [loadForProduct, loadForCategory, productId, categoryId]);

  /*
   * What was loaded. While creating, the suggestions come from the chosen
   * category and there are no answers yet, so they are shaped as slots and the
   * same form renders either way. Memoised on the store's own arrays: rebuilt
   * on every render, it once reset the form on every render and threw typed
   * values away.
   */
  const loaded = useMemo<Slot[]>(() => {
    if (productId) return productSlotsFor === productId ? productSlots : [];
    if (!categoryId || categoryAttributesFor !== categoryId) return [];
    return categoryAttributes.map((attribute) => fromAttribute(attribute, true));
  }, [productId, productSlots, productSlotsFor, categoryId, categoryAttributes, categoryAttributesFor]);

  // A saved product's answers arrived — a first load, a save, a reload — so
  // what was being edited over the old ones goes with them. Adjusted while
  // rendering, not in an effect. A new product keeps its values when its
  // category changes: they are its own now, whatever suggested them.
  const [baseline, setBaseline] = useState(loaded);
  if (baseline !== loaded) {
    setBaseline(loaded);
    if (productId) {
      setEdits({});
      setAdded([]);
      setDropped([]);
    }
  }

  const libraryById = useMemo(() => new Map(library.map((attribute) => [attribute.id, attribute])), [library]);

  const suggested = loaded.filter((slot) => slot.fromCategory);
  const suggestedIds = new Set(suggested.map((slot) => slot.attributeId));
  const loadedOwn = loaded.filter((slot) => !slot.fromCategory);
  const ownIds = [
    ...new Set([
      ...loadedOwn.map((slot) => slot.attributeId),
      ...added,
      // Answered is the product's own, whatever asked for it.
      ...Object.keys(edits).filter((id) => hasValue(edits[id])),
    ]),
  ].filter((id) => !suggestedIds.has(id) && !dropped.includes(id));
  const own = ownIds.flatMap((id): Slot[] => {
    const slot = loadedOwn.find((entry) => entry.attributeId === id);
    if (slot) return [slot];
    const attribute = libraryById.get(id);
    return attribute ? [fromAttribute(attribute, false)] : [];
  });

  const valueOf = (slot: Slot) => edits[slot.attributeId] ?? toValue(slot);
  const values = [...suggested, ...own].map(valueOf).filter(hasValue);
  const dirty = Object.keys(edits).length > 0 || added.length > 0 || dropped.length > 0;

  const shown = new Set([...suggestedIds, ...ownIds]);
  const candidates = library.filter((attribute) => attribute.isActive && !shown.has(attribute.id));

  const nameOf = (slot: Slot) => attributeName(libraryById.get(slot.attributeId) ?? slot, locale);

  const change = (attributeId: string, next: ProductAttributeValueInput) => {
    const nextEdits = { ...edits, [attributeId]: next };
    setEdits(nextEdits);
    setJustSaved(false);
    // A new product has no answers but these, so they are the whole list.
    if (!productId) onDraftChange?.(Object.values(nextEdits).filter(hasValue));
  };

  const remove = (attributeId: string) => {
    change(attributeId, emptyValue(attributeId));
    setAdded((current) => current.filter((id) => id !== attributeId));
    setDropped((current) => (current.includes(attributeId) ? current : [...current, attributeId]));
  };

  const addFromLibrary = (ids: string[]) => {
    setAdded((current) => [...new Set([...current, ...ids])]);
    setDropped((current) => current.filter((id) => !ids.includes(id)));
    setJustSaved(false);
    setPicking(false);
  };

  const discard = () => {
    setEdits({});
    setAdded([]);
    setDropped([]);
  };

  async function save() {
    if (productId && (await setForProduct(productId, values))) setJustSaved(true);
  }

  const row = (slot: Slot, removable: boolean) => (
    <SpecificationRow
      key={slot.attributeId}
      slot={slot}
      name={nameOf(slot)}
      value={valueOf(slot)}
      locale={locale}
      onChange={(patch) => change(slot.attributeId, { ...valueOf(slot), ...patch, attributeId: slot.attributeId })}
      onRemove={removable ? () => remove(slot.attributeId) : undefined}
    />
  );

  return (
    <Section
      title={t('specifications')}
      subtitle={t('specsCardSubtitle')}
      action={
        <>
          {/* A saved product's category can change on the details tab; this
              picks up what the new one suggests. */}
          {productId ? (
            <IconButton label={t('specsReload')} onClick={() => void loadForProduct(productId)} className="size-8">
              <ReloadIcon className="size-4" />
            </IconButton>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => setPicking(true)}>
            <PlusIcon className="size-4" />
            {t('addSpecification')}
          </Button>
        </>
      }
      flush
    >
      {error && !productId ? (
        <div className="border-t border-line px-5 py-3">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {suggested.length === 0 && own.length === 0 ? (
        <div className="flex flex-col border-t border-line">
          <EmptyState
            title={t('noSpecifications')}
            body={productId || categoryId ? t('specsEmptyHint') : t('specsChooseCategoryHint')}
            icon={<TagIcon />}
          />
        </div>
      ) : (
        <>
          {suggested.length > 0 ? (
            <RowGroup title={t('specsFromCategory')}>{suggested.map((slot) => row(slot, false))}</RowGroup>
          ) : null}
          {own.length > 0 ? (
            <RowGroup title={t('specsAddedHere')} hint={t('specsAddedHint')}>
              {own.map((slot) => row(slot, true))}
            </RowGroup>
          ) : null}
        </>
      )}

      {/* Only a saved product saves from here; while creating, the product's
          own save applies these along with everything else. */}
      {productId ? (
        <div
          className={cx(
            'flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3',
            dirty && 'bg-brand-50/40',
          )}
        >
          <span className="me-auto min-w-0 text-sm">
            {error ? (
              <Alert>{error}</Alert>
            ) : dirty ? (
              <span className="text-ink-muted">{t('unsavedChanges')}</span>
            ) : justSaved ? (
              <span className="text-success">✓ {t('saved')}</span>
            ) : null}
          </span>
          {dirty ? (
            <Button variant="secondary" size="sm" onClick={discard} disabled={saving}>
              {t('discard')}
            </Button>
          ) : null}
          <Button size="sm" onClick={() => void save()} disabled={saving || !dirty}>
            {saving ? t('saving') : t('saveSpecifications')}
          </Button>
        </div>
      ) : null}

      {picking ? (
        <SpecificationPickerSheet
          candidates={candidates}
          onAdd={addFromLibrary}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </Section>
  );
}

/** A band naming a group of rows, then the rows — the shipping table's colour bands. */
function RowGroup({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-t border-line bg-surface-sunken/70 px-5 py-2">
        <h3 className="text-xs font-semibold text-ink">{title}</h3>
        {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
      </div>
      <ul className="flex flex-col">{children}</ul>
    </div>
  );
}

function SpecificationRow({
  slot,
  name,
  value,
  locale,
  onChange,
  onRemove,
}: {
  slot: Slot;
  name: string;
  value: ProductAttributeValueInput;
  locale: AdminLocale;
  onChange: (patch: Partial<ProductAttributeValueInput>) => void;
  onRemove?: () => void;
}) {
  const t = useT();
  const id = `spec-${slot.attributeId}`;
  // Only a single control can be the target of a <label>; a group is named by it.
  const labelled = slot.type === 'TEXT' || slot.type === 'NUMBER' || slot.type === 'SELECT';
  const caption = (
    <>
      {name}
      {slot.unit ? (
        <span dir="ltr" className="ms-1 font-normal text-ink-faint">
          ({slot.unit})
        </span>
      ) : null}
    </>
  );

  return (
    <li className="grid gap-2 border-t border-line px-5 py-3 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)_2rem] sm:items-center sm:gap-4">
      <div className="min-w-0">
        {labelled ? (
          <label htmlFor={id} className="text-sm font-medium text-ink">
            {caption}
          </label>
        ) : (
          <span id={`${id}-label`} className="text-sm font-medium text-ink">
            {caption}
          </span>
        )}
        {slot.isRequired || slot.isActive === false ? (
          <span className="mt-1 flex flex-wrap gap-1.5">
            {slot.isRequired ? <Pill tone="warning">{t('required')}</Pill> : null}
            {slot.isActive === false ? <Pill tone="warning">{t('specRetiredClear')}</Pill> : null}
          </span>
        ) : null}
      </div>

      <div className="min-w-0">
        <SlotInput id={id} name={name} slot={slot} value={value} locale={locale} onChange={onChange} />
      </div>

      <div className="flex justify-end">
        {onRemove ? (
          <IconButton
            label={`${t('removeSpecification')} — ${name}`}
            onClick={onRemove}
            className="size-8 border-0 hover:text-danger"
          >
            <TrashIcon className="size-4" />
          </IconButton>
        ) : null}
      </div>
    </li>
  );
}

function SlotInput({
  id,
  name,
  slot,
  value,
  locale,
  onChange,
}: {
  id: string;
  name: string;
  slot: Slot;
  value: ProductAttributeValueInput;
  locale: AdminLocale;
  onChange: (patch: Partial<ProductAttributeValueInput>) => void;
}) {
  const t = useT();

  switch (slot.type) {
    case 'SELECT':
      return (
        <Select
          id={id}
          value={value.optionIds?.[0] ?? ''}
          onChange={(event) => onChange({ optionIds: event.target.value ? [event.target.value] : [] })}
          className="w-full sm:max-w-80"
        >
          <option value="">{t('choose')}</option>
          {slot.options.map((option) => (
            <option key={option.id} value={option.id}>
              {optionName(option, locale)}
            </option>
          ))}
        </Select>
      );

    case 'MULTI_SELECT': {
      // Chips rather than a native multi-select: every choice in view, and
      // picking one does not need a modifier key.
      const selected = value.optionIds ?? [];
      return (
        <div role="group" aria-labelledby={`${id}-label`} className="flex flex-wrap gap-1.5">
          {slot.options.length === 0 ? <span className="text-sm text-ink-faint">{t('dash')}</span> : null}
          {slot.options.map((option) => {
            const on = selected.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  onChange({
                    // Kept in the attribute's own order, so a save does not reshuffle it.
                    optionIds: slot.options
                      .filter((entry) => (entry.id === option.id ? !on : selected.includes(entry.id)))
                      .map((entry) => entry.id),
                  })
                }
                className={cx(
                  'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
                  on
                    ? 'border-brand-600 bg-brand-50 text-brand-700'
                    : 'border-line bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink',
                )}
              >
                {on ? <CheckIcon className="size-3.5" /> : null}
                {optionName(option, locale)}
              </button>
            );
          })}
        </div>
      );
    }

    case 'BOOLEAN':
      // Three states, not two: "not stated" is an answer a switch cannot give.
      return (
        <Segmented
          label={name}
          value={value.valueBoolean === true ? 'yes' : value.valueBoolean === false ? 'no' : 'unset'}
          onChange={(choice) => onChange({ valueBoolean: choice === 'unset' ? null : choice === 'yes' })}
          items={[
            { id: 'yes', label: t('yes') },
            { id: 'no', label: t('no') },
            { id: 'unset', label: t('dash') },
          ]}
        />
      );

    case 'NUMBER':
      return <NumberInput id={id} unit={slot.unit} value={value.valueNumber ?? null} onChange={onChange} />;

    case 'TEXT':
      return <Input id={id} value={value.valueText ?? ''} onChange={(event) => onChange({ valueText: event.target.value })} />;
  }
}

/**
 * A number typed as text, so "1." and "-" survive while they are being typed —
 * parsing every keystroke into a number threw them away. Arabic-Indic digits
 * and the Arabic decimal mark are read as what they are.
 */
function NumberInput({
  id,
  unit,
  value,
  onChange,
}: {
  id: string;
  unit: string | null;
  value: number | null;
  onChange: (patch: Partial<ProductAttributeValueInput>) => void;
}) {
  const [text, setText] = useState(value === null ? '' : String(value));

  // The value moved from outside — a reload, a discard — so the box follows it.
  if (parseNumber(text) !== value) setText(value === null ? '' : String(value));

  return (
    <div dir="ltr" className="relative w-full sm:max-w-60">
      <Input
        id={id}
        dir="ltr"
        inputMode="decimal"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          onChange({ valueNumber: parseNumber(event.target.value) });
        }}
        className={cx('w-full', unit && 'pe-12')}
      />
      {unit ? (
        <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-sm text-ink-faint">
          {unit}
        </span>
      ) : null}
    </div>
  );
}

function parseNumber(raw: string): number | null {
  const normalised = raw
    .trim()
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[٫,]/g, '.');
  // An exponent too: the API may hand back 1e-7, and the box must read what it shows.
  if (!/^-?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(normalised)) return null;
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? parsed : null;
}

// ─── The library picker ──────────────────────────────────────────────────────

/**
 * Adds specifications from the library — searched by name in either language
 * or by code, several at once — or, for the owner, creates one on the spot.
 *
 * Shared with the category template editor: a product and a category choose
 * from the same library the same way. The library is small, so it is filtered
 * here rather than searched on the server.
 */
export function SpecificationPickerSheet({
  candidates,
  subtitle,
  onAdd,
  onClose,
}: {
  /** What may be added: active, and not already there. */
  candidates: AttributeDto[];
  subtitle?: string;
  onAdd: (attributeIds: string[]) => void;
  onClose: () => void;
}) {
  const isOwner = useAuthStore((state) => state.user?.role === 'OWNER');
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  if (creating) {
    return (
      <NewSpecificationSheet
        onBack={() => setCreating(false)}
        // What was ticked before is added with it: creating one is not a reason to lose them.
        onCreated={(attribute) => onAdd([...selected, attribute.id])}
      />
    );
  }

  const needle = search.trim().toLowerCase();
  const shown = needle
    ? candidates.filter((attribute) =>
        [attribute.code, attribute.label, attribute.translations.ar, attribute.translations.en].some((text) =>
          text?.toLowerCase().includes(needle),
        ),
      )
    : candidates;

  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));

  return (
    <Sheet
      open
      title={t('addSpecification')}
      subtitle={subtitle ?? t('specsPickerHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button disabled={selected.length === 0} onClick={() => onAdd(selected)}>
            {selected.length > 0 ? t('addCount', { count: count(selected.length, locale) }) : t('addSpecification')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-48 flex-1">
            <SearchIcon className="pointer-events-none absolute inset-y-0 start-3 my-auto size-4 text-ink-faint" />
            <Input
              type="search"
              aria-label={t('searchLabel')}
              placeholder={t('specsSearch')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="ps-9"
            />
          </div>
          {isOwner ? (
            <Button variant="secondary" onClick={() => setCreating(true)}>
              <PlusIcon className="size-4" />
              {t('newSpecification')}
            </Button>
          ) : null}
        </div>

        {shown.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-muted">
            {candidates.length === 0 ? t('specsAllUsed') : t('specsNoMatch')}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-control border border-line">
            {shown.map((attribute) => (
              <li key={attribute.id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-surface-sunken">
                  <input
                    type="checkbox"
                    checked={selected.includes(attribute.id)}
                    onChange={() => toggle(attribute.id)}
                    className="size-4 shrink-0"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{attributeName(attribute, locale)}</span>
                    <span dir="ltr" className="block truncate text-xs text-ink-faint">
                      {attribute.code}
                      {attribute.unit ? ` · ${attribute.unit}` : ''}
                    </span>
                  </span>
                  <Pill tone="neutral">{t(`attrType${attribute.type}` as 'attrTypeTEXT')}</Pill>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}

const TYPES: AttributeType[] = ['TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT'];

interface NewDraft {
  ar: string;
  en: string;
  /** Typed by hand; until then the code follows the English name. */
  code: string | null;
  type: AttributeType;
  unit: string;
  isFilterable: boolean;
  options: { ar: string; en: string }[];
}

/**
 * A new library attribute, made without leaving the product or category that
 * needs it. Owner work (it shapes every product screen), so only an owner is
 * offered it; the API refuses anyone else as well.
 */
function NewSpecificationSheet({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  onCreated: (attribute: AttributeDto) => void;
}) {
  const { create, saving, error } = useAttributesStore();
  const t = useT();

  const [draft, setDraft] = useState<NewDraft>({
    ar: '',
    en: '',
    code: null,
    type: 'TEXT',
    unit: '',
    isFilterable: false,
    options: [{ ar: '', en: '' }],
  });
  const [tried, setTried] = useState(false);
  const set = (patch: Partial<NewDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const setOption = (index: number, patch: Partial<NewDraft['options'][number]>) =>
    set({ options: draft.options.map((option, position) => (position === index ? { ...option, ...patch } : option)) });

  const code = (draft.code ?? toCode(draft.en)).trim();
  const isChooser = draft.type === 'SELECT' || draft.type === 'MULTI_SELECT';
  const options = draft.options.filter((option) => option.en.trim() !== '');
  const valid = draft.en.trim() !== '' && code !== '' && (!isChooser || options.length > 0);

  async function submit() {
    setTried(true);
    const created = await create({
      code,
      // English is the fallback every other language falls back to; Arabic
      // left blank reads as the English rather than as nothing.
      translations: { ar: draft.ar.trim() || draft.en.trim(), en: draft.en.trim() },
      type: draft.type,
      unit: draft.type === 'NUMBER' ? draft.unit.trim() || null : null,
      isFilterable: draft.isFilterable,
      options: isChooser ? withCodes(options) : undefined,
    });
    if (created) onCreated(created);
  }

  return (
    <Sheet
      open
      size="lg"
      title={t('newSpecification')}
      subtitle={t('newSpecificationHint')}
      onClose={onBack}
      footer={
        <>
          {tried && error ? (
            <span className="me-auto">
              <Alert>{error}</Alert>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onBack} disabled={saving}>
            {t('back')}
          </Button>
          <Button disabled={saving || !valid} onClick={() => void submit()}>
            {saving ? t('saving') : t('createAndAdd')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('nameArabic')} htmlFor="spec-new-ar">
            <Input id="spec-new-ar" dir="rtl" value={draft.ar} onChange={(event) => set({ ar: event.target.value })} />
          </Field>
          <Field label={t('nameEnglish')} htmlFor="spec-new-en">
            <Input id="spec-new-en" dir="ltr" value={draft.en} onChange={(event) => set({ en: event.target.value })} />
          </Field>
          <Field label={t('attributeCode')} htmlFor="spec-new-code" hint={t('attributeCodeHint')}>
            <Input
              id="spec-new-code"
              dir="ltr"
              value={draft.code ?? toCode(draft.en)}
              onChange={(event) => set({ code: event.target.value })}
            />
          </Field>
          <Field label={t('attributeType')} htmlFor="spec-new-type">
            <Select
              id="spec-new-type"
              value={draft.type}
              onChange={(event) => set({ type: event.target.value as AttributeType })}
              className="w-full"
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`attrType${type}` as 'attrTypeTEXT')}
                </option>
              ))}
            </Select>
          </Field>
          {draft.type === 'NUMBER' ? (
            <Field label={t('measurementUnit')} htmlFor="spec-new-unit" hint={t('unitHint')}>
              <Input
                id="spec-new-unit"
                dir="ltr"
                value={draft.unit}
                onChange={(event) => set({ unit: event.target.value })}
              />
            </Field>
          ) : null}
        </div>

        <div className="flex items-start justify-between gap-3 rounded-control border border-line px-3 py-2.5">
          <span className="min-w-0">
            <span className="block text-sm text-ink">{t('filterable')}</span>
            <span className="block text-xs text-ink-muted">{t('filterableHint')}</span>
          </span>
          <Switch
            label={t('filterable')}
            checked={draft.isFilterable}
            onChange={(isFilterable) => set({ isFilterable })}
          />
        </div>

        {isChooser ? (
          <div className="border-t border-line pt-5">
            <h3 className="mb-1 text-sm font-medium text-ink">{t('choices')}</h3>
            <p className="mb-3 text-xs text-ink-muted">{t('specChoicesHint')}</p>

            <div className="flex flex-col gap-2">
              {draft.options.map((option, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Input
                    dir="rtl"
                    aria-label={t('nameArabic')}
                    placeholder={t('nameArabic')}
                    value={option.ar}
                    onChange={(event) => setOption(index, { ar: event.target.value })}
                  />
                  <Input
                    dir="ltr"
                    aria-label={t('nameEnglish')}
                    placeholder={t('nameEnglish')}
                    value={option.en}
                    onChange={(event) => setOption(index, { en: event.target.value })}
                  />
                  <IconButton
                    label={t('remove')}
                    onClick={() => set({ options: draft.options.filter((_, position) => position !== index) })}
                    className="border-0 hover:text-danger"
                  >
                    <TrashIcon className="size-4" />
                  </IconButton>
                </div>
              ))}
            </div>

            <div className="mt-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => set({ options: [...draft.options, { ar: '', en: '' }] })}
              >
                <PlusIcon className="size-4" />
                {t('addChoice')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** A slot as the API sends it now: `isActive` false is a retired attribute the product still answers. */
type Slot = ProductAttributeSlotDto & { isActive?: boolean };

/**
 * An attribute's name in the reader's language, from both as stored. The
 * resolved `label` is only the fallback: a save answers in the API's default
 * language, whatever the screen's is.
 */
export function attributeName(
  attribute: { label: string; translations?: { ar: string; en: string } },
  locale: AdminLocale,
): string {
  return attribute.translations?.[locale] || attribute.label;
}

function optionName(option: AttributeOptionDto, locale: AdminLocale): string {
  return option.translations?.[locale] || option.label;
}

function fromAttribute(attribute: AttributeDto, fromCategory: boolean): Slot {
  return {
    attributeId: attribute.id,
    code: attribute.code,
    label: attribute.label,
    type: attribute.type,
    unit: attribute.unit,
    options: attribute.options,
    display: '',
    valueText: null,
    valueNumber: null,
    valueBoolean: null,
    optionIds: [],
    isRequired: fromCategory && (attribute.isRequired ?? false),
    fromCategory,
    isActive: attribute.isActive,
  };
}

function toValue(slot: Slot): ProductAttributeValueInput {
  return {
    attributeId: slot.attributeId,
    valueText: slot.valueText,
    valueNumber: slot.valueNumber,
    valueBoolean: slot.valueBoolean,
    optionIds: slot.optionIds,
  };
}

function emptyValue(attributeId: string): ProductAttributeValueInput {
  return { attributeId, valueText: null, valueNumber: null, valueBoolean: null, optionIds: [] };
}

/** Whether there is anything to save — the API writes nothing for an empty one. */
function hasValue(value: ProductAttributeValueInput): boolean {
  return (
    Boolean(value.valueText?.trim()) ||
    (value.valueNumber !== null && value.valueNumber !== undefined) ||
    (value.valueBoolean !== null && value.valueBoolean !== undefined) ||
    (value.optionIds?.length ?? 0) > 0
  );
}

/** "Warranty period" → `warranty_period`: the library's codes are snake case. */
function toCode(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

/** Choices get codes from their English names, unique within the attribute. */
function withCodes(options: { ar: string; en: string }[]) {
  const taken = new Set<string>();

  return options.map((option, index) => {
    const base = toCode(option.en) || `option_${index + 1}`;
    let code = base;
    for (let suffix = 2; taken.has(code); suffix += 1) code = `${base}_${suffix}`;
    taken.add(code);

    return { code, translations: { ar: option.ar.trim() || option.en.trim(), en: option.en.trim() } };
  });
}

/** Two arrows chasing each other — no shared icon says "reload" yet. */
function ReloadIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? 'size-5'}
    >
      <path d="M20 11a8 8 0 0 0-14.3-4.9L4 8" />
      <path d="M4 4v4h4" />
      <path d="M4 13a8 8 0 0 0 14.3 4.9L20 16" />
      <path d="M20 20v-4h-4" />
    </svg>
  );
}
